import { useEffect } from 'react';
import { useBasket } from '~/features/basket';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import { useEvents } from '~/shared/hooks/useEvents';
import { _transformProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import {
    _matchCouponsFromCartWithProducts,
    _getCouponsFromOrder,
} from '~/shared/hooks/useEvents/useGTMEvents/helper';
import { useFrame } from '~/shared/utils';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { useTranslation } from '~/shared/utils/translation';
import { useLocalStorage } from '~/shared/hooks/useLocalStorage';
import { DeliveryAddressOption } from '../..';
import { useCheckout } from '../../hooks/useCheckout';
import { DeliveryMethod } from '../../models/checkout';
import { useCheckoutState } from '../../hooks/useCheckoutState';

export const DeliverInformation = () => {
    const {
        goForward,
        setShipmentId,
        setNumberOfShipments,
        getCurrentShipmentIndex,
        resetCurrentShipmentStep,
        setDeliveryMethod,
        carriers,
    } = useCheckout();

    const { shipmentId, setCurrentShipmentStep, deliveryMethod, deliveryAddress } =
        useCheckoutState();
    const [, setPrevCCStoreId] = useLocalStorage<string>('previouslySelectedCCStoreId');

    const basket = useBasket();
    const { products } = useProducts();
    const { data: frame } = useFrame();
    const { addShippingInfoEvent } = useEvents(frame);
    const { trackAndDisplayError: trackError } = useErrorHandler();
    const { translate } = useTranslation();

    const handleShipmentSubmit = () => {
        if (basket.shipments) {
            const numberOfShipments = basket.shipments.length;
            const indexOfCurrent = getCurrentShipmentIndex(basket.shipments);

            if (products) {
                try {
                    const couponCodes = _matchCouponsFromCartWithProducts(
                        products,
                        basket.couponItems,
                    );
                    const orderCoupons = _getCouponsFromOrder(basket);
                    addShippingInfoEvent(
                        products.map((item, index) => {
                            item.coupon = couponCodes?.appliedCoupons[item.id]?.couponCode;
                            return _transformProductToDataLayer(item, item.quantity, index);
                        }),
                        String(basket.orderTotal),
                        orderCoupons,
                        basket.shipments[indexOfCurrent - 1]?.shippingMethod?.name as string,
                    );
                } catch (e) {
                    console.warn(e);
                }
            }

            if (indexOfCurrent === numberOfShipments) {
                // Remember the chosen store so it can be prepended in the reservation drawer per IMC-2711 ("Previously selected C&C store").
                if (
                    deliveryMethod === 'clickAndCollect' &&
                    deliveryAddress?.deliveryType === 'store'
                ) {
                    setPrevCCStoreId(deliveryAddress.storeId);
                }
                goForward();
            } else {
                setCurrentShipmentStep(indexOfCurrent + 1);
                setShipmentId(basket.shipments[indexOfCurrent]?.shipmentId as string);

                if (basket.c_suggestedStore) {
                    window.setTimeout(() => {
                        basket.setSuggestedStore(null).catch((error) => {
                            console.warn('Could not clear suggested store', error);
                        });
                    }, 0);
                }
            }
            window.scrollTo(0, 0);
        }
    };

    const handleShowPreviousShipment = () => {
        try {
            if (basket.shipments) {
                const indexOfCurrent = getCurrentShipmentIndex(basket.shipments);
                const newIndex = indexOfCurrent - 1;

                if (newIndex >= 1) {
                    setCurrentShipmentStep(newIndex);

                    setShipmentId(basket.shipments[newIndex - 1].shipmentId as string);
                    window.scrollTo(0, 0);
                }
            }
        } catch (e) {
            trackError(new Error(translate('message.generalErrorMessage')), e);
        }
    };

    const getDefaultDeliveryMethod = (carriersByType: any) => {
        if (!carriersByType) return;

        if (carriersByType['clickAndCollect'] && carriersByType['clickAndCollect'].length > 0) {
            return 'clickAndCollect';
        }

        const method = (Object.keys(carriersByType) as DeliveryMethod[]).find(
            (key) => carriersByType[key].length > 0,
        );

        return method;
    };

    useEffect(() => {
        resetCurrentShipmentStep();
        window.scrollTo(0, 0);
        if (basket.shipments) {
            setNumberOfShipments(basket.shipments?.length);
            const currentShipmentId = basket.shipments[0]?.shipmentId as string;

            if (currentShipmentId) {
                setShipmentId(currentShipmentId);
                const defaultDeliveryMethod = getDefaultDeliveryMethod(carriers);
                if (defaultDeliveryMethod) {
                    setDeliveryMethod(defaultDeliveryMethod);
                }
            }
        }
    }, []);

    if (!basket.shipments?.length) return null;

    return (
        <DeliveryAddressOption
            numberOfShipments={basket.shipments?.length}
            currentShipmentIndex={getCurrentShipmentIndex(basket.shipments)}
            showPreviousShipment={handleShowPreviousShipment}
            onSubmit={handleShipmentSubmit}
            shipmentId={shipmentId}
        />
    );
};
